// apps/client/src/hooks/useEdmImport.ts
//
// eDM contact import over the `edmImportAction` callable. The order is fixed
// by the storage rule, which admits an upload only when the import doc
// already exists:
//
//   beginImport({ ext })            -> { importId }   (creates the doc)
//   upload edm-imports/{companyId}/{importId}/source.{ext}
//   previewImport({ importId })     -> mapping suggestion + sample rows
//   confirmMapping({ importId, mapping, fileRole, consent? }) -> counters
//
// Progress during confirmMapping is not observable (edm_imports is client
// read false), so the page shows a spinner and relies on a re-call with the
// same params, which returns the stored counters once the import completes.

import { useCallback, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { ref as storageRef, uploadBytesResumable } from 'firebase/storage';
import { functions, storage } from '../lib/firebase';
import { EDM_CALLABLE_OPTIONS } from './useEdmCampaigns';
import { EDM_IMPORT_ATTESTATION_VERSION } from '../utils/edm';
import type { EdmFileRole } from '../utils/edm';

export type EdmImportExt = 'csv' | 'xlsx' | 'pdf';

export type EdmMappingTarget =
  | 'email'
  | 'firstName'
  | 'lastName'
  | 'phone'
  | 'tags'
  | 'status'
  | 'unsubscribed'
  | 'bounced'
  | 'complaint'
  | 'broker'
  | 'ignore';

/** Source column name -> target field. */
export type EdmMapping = Record<string, EdmMappingTarget>;

export interface EdmImportPreview {
  suggestedMapping: EdmMapping;
  sampleRows: Array<Record<string, string>>;
  columns: string[];
  totalRows: number;
  requiresNegativeRole: boolean;
  fileSha256: string;
  /** Broker column values that match no active company member (imported unassigned). */
  brokerUnmatched?: number;
}

export interface EdmImportCounters {
  total: number;
  imported: number;
  suppressed: number;
  duplicate: number;
  duplicateLead: number;
  invalid: number;
  excluded: number;
}

export interface EdmImportResult {
  status: 'completed' | 'failed';
  counters: EdmImportCounters;
}

export interface EdmConfirmParams {
  importId: string;
  mapping: EdmMapping;
  fileRole: EdmFileRole;
  /** Sent only for fileRole 'contacts'. */
  attestationText?: string;
}

export const EDM_IMPORT_MAX_BYTES = 10 * 1024 * 1024;

const CONTENT_TYPE: Record<EdmImportExt, string> = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf',
};

/** 'csv' | 'xlsx' | 'pdf' from a filename, or null for anything else. */
export function edmImportExt(fileName: string): EdmImportExt | null {
  const m = /\.([a-z0-9]+)$/i.exec(fileName);
  const ext = m?.[1]?.toLowerCase();
  return ext === 'csv' || ext === 'xlsx' || ext === 'pdf' ? ext : null;
}

export interface EdmImportApi {
  beginImport: (ext: EdmImportExt) => Promise<{ importId: string }>;
  upload: (importId: string, ext: EdmImportExt, file: File, onProgress: (pct: number) => void) => Promise<void>;
  previewImport: (importId: string) => Promise<EdmImportPreview>;
  confirmMapping: (params: EdmConfirmParams) => Promise<EdmImportResult>;
}

export function useEdmImport(companyId: string | undefined): EdmImportApi {
  const invoke = useCallback(
    async <Res,>(action: string, payload: Record<string, unknown>): Promise<Res> => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<Record<string, unknown>, Res>(
        functions,
        'edmImportAction',
        EDM_CALLABLE_OPTIONS,
      );
      const res = await fn({ action, companyId, ...payload });
      return res.data;
    },
    [companyId],
  );

  const upload = useCallback<EdmImportApi['upload']>(
    (importId, ext, file, onProgress) => {
      if (!companyId) return Promise.reject(new Error('companyId required'));
      const ref = storageRef(storage, `edm-imports/${companyId}/${importId}/source.${ext}`);
      const task = uploadBytesResumable(ref, file, { contentType: CONTENT_TYPE[ext] });
      return new Promise<void>((resolve, reject) => {
        task.on(
          'state_changed',
          (snap) => {
            const pct = snap.totalBytes > 0 ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100) : 0;
            onProgress(pct);
          },
          reject,
          () => resolve(),
        );
      });
    },
    [companyId],
  );

  return useMemo<EdmImportApi>(
    () => ({
      beginImport: (ext) => invoke('beginImport', { ext }),
      upload,
      previewImport: (importId) => invoke('previewImport', { importId }),
      confirmMapping: ({ importId, mapping, fileRole, attestationText }) =>
        invoke('confirmMapping', {
          importId,
          mapping,
          fileRole,
          ...(fileRole === 'contacts' && attestationText !== undefined
            ? { consent: { attestationVersion: EDM_IMPORT_ATTESTATION_VERSION, attestationText } }
            : {}),
        }),
    }),
    [invoke, upload],
  );
}
